import React from 'react';
import { Button, Col, Dropdown } from 'react-bootstrap';
import { ICompanyUser } from '../../dto/ICompanyUser';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { UserRoles } from '../../../../../enum/UserRoles';
import { FaEllipsisV, FaUserCheck } from 'react-icons/fa';
import { FaRegTrashCan, FaUserGroup, FaUserXmark } from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { formatGroupOptions } from '../../../../../shared/functions/Functions';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';

interface IUserAndRoles {
    companyUser: ICompanyUser;
    disableUser: (id: string) => void;
    deleteUserAction: (companyUser?: ICompanyUser) => void;
    editUser: (companyUser?: ICompanyUser) => void;
    manageGroupsAction: (companyUser?: ICompanyUser) => void;
    isMangeGroupsVisible?: boolean;
}

export function UserAndRolesCard({
    companyUser,
    disableUser,
    deleteUserAction,
    editUser,
    manageGroupsAction,
    isMangeGroupsVisible,
}: IUserAndRoles) {
    const { t } = useTranslation();
    const loggedUserId = useSelector((reduxState: IAllStates) => reduxState.auth?.user?.id);
    const locale = useSelector((state: IAllStates) => state.auth.user.locale);

    const isAdmin = [UserRoles.AGENCY_MASTER, UserRoles.BRAND_MASTER].includes(
        companyUser?.userType ?? UserRoles.BRAND
    );
    const hasGroups = companyUser?.groups && companyUser?.groups?.length > 0;
    const isDe = locale === 'de';

    const isLoggedUser = loggedUserId === Number(companyUser.id);

    const formattedOptions = formatGroupOptions(companyUser?.groups ?? [], isDe);
    const truncatedOptions = formatGroupOptions(companyUser?.groups ?? [], isDe, 40);
    const isGroupTruncated = formattedOptions.length > 40;

    return (
        <Col lg={3} className="mb-4">
            <CardLayout
                classCard={isLoggedUser ? 'border-primary' : ''}
                classBody="d-flex flex-column justify-content-center align-items-center"
            >
                {isAdmin && <p className="status accepted place-top-left">Admin</p>}
                {!isAdmin && (
                    <Dropdown className="btn-group top-right-icon" drop="down">
                        <Dropdown.Toggle variant="light" id="dropdown-basic">
                            <FaEllipsisV className="text-secondary" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => disableUser(companyUser?.id ?? '')}>
                                {companyUser?.isEnabled ? (
                                    <FaUserXmark className="mr-1" />
                                ) : (
                                    <FaUserCheck className="mr-1" />
                                )}
                                {companyUser?.isEnabled
                                    ? `${t('general.button.disable')}`
                                    : `${t('general.button.enable')}`}
                            </Dropdown.Item>
                            {isMangeGroupsVisible && (
                                <Dropdown.Item onClick={() => manageGroupsAction(companyUser)}>
                                    <FaUserGroup className="mr-1" />
                                    {t('brand.account.manage_groups')}{' '}
                                </Dropdown.Item>
                            )}
                            {companyUser?.canBeDeleted && (
                                <Dropdown.Item className="text-danger" onClick={() => deleteUserAction(companyUser)}>
                                    <FaRegTrashCan className="mr-1" />

                                    {t('general.button.delete')}
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                <CustomImageHandler
                    altTag={companyUser?.profilePhoto?.name ?? ''}
                    classes="user-img-rounded-lg"
                    thumbnailPath={companyUser?.profilePhoto?.thumbnail as string}
                    photoPath={companyUser?.profilePhoto?.thumbnail as string}
                    name={companyUser?.firstName}
                />
                <div className="text-center mt-3">
                    <h6 className="word-break mb-0">
                        {companyUser?.firstName} {companyUser?.lastName}
                    </h6>
                    {companyUser?.position ? (
                        <div className="mb-0 text-primary">{t(`general.positions.${companyUser?.position}`)}</div>
                    ) : (
                        <span className="text-primary">-</span>
                    )}
                    <div className="d-flex flex-column align-items-center">
                        {companyUser?.email && !hasGroups && (
                            <div className="mb-0 text-muted">{companyUser?.email}</div>
                        )}
                        {hasGroups && (
                            <TooltipOverlay content={isGroupTruncated ? formattedOptions : ''}>
                                <p className="mb-1 text-muted justify-content-center">{truncatedOptions || '-'}</p>
                            </TooltipOverlay>
                        )}
                    </div>
                </div>
                <Button variant="outline-primary" className="w-100 mt-2" onClick={() => editUser(companyUser)}>
                    {t('general.button.edit')}
                </Button>
                {/*</div>*/}
            </CardLayout>
        </Col>
    );
}
